import { SVGProps, useId } from 'react';

import { brand } from '../../styles/tokens';
import {
  LOCKUP_HEIGHT,
  LOCKUP_MARK_SIZE,
  LOCKUP_WIDTH,
  MARK_RAYS,
  MARK_SIZE,
  WORDMARK,
} from './paths';

export type LogoVariant = 'lockup' | 'mark';

/**
 * Which surface the logo sits on. The mark keeps its own square so it is
 * legible anywhere; only the wordmark changes color (brand board, page 8).
 */
export type LogoTone = 'onDark' | 'onLight' | 'onVolt';

export interface AvaliaHubLogoProps
  extends Omit<SVGProps<SVGSVGElement>, 'height' | 'width'> {
  variant?: LogoVariant;
  tone?: LogoTone;
  /** Rendered height in px; width follows the variant's aspect ratio. */
  height?: number;
  /**
   * Accessible name. When given the logo is announced as an image; when
   * omitted it is decorative and hidden from assistive technology.
   */
  title?: string;
}

export const LOCKUP_RATIO = LOCKUP_WIDTH / LOCKUP_HEIGHT;

const WORDMARK_COLOR: Record<LogoTone, string> = {
  onDark: brand.white,
  onLight: brand.ink,
  onVolt: brand.deepGreen,
};

/**
 * The AvaliaHub logo as inline SVG so every color comes from the brand tokens
 * and the wordmark can flip between light and dark surfaces. The hub symbol
 * keeps the full Volt on Canopy in every tone: the identity colors stay
 * untouched in the logo even though the UI action color is tempered.
 */
export function AvaliaHubLogo({
  variant = 'lockup',
  tone = 'onLight',
  height = 32,
  title,
  ...svgProps
}: AvaliaHubLogoProps): JSX.Element {
  const titleId = useId();
  const isLockup = variant === 'lockup';

  const viewBox = isLockup
    ? `0 0 ${LOCKUP_WIDTH} ${LOCKUP_HEIGHT}`
    : `0 0 ${MARK_SIZE} ${MARK_SIZE}`;
  const width = isLockup ? height * LOCKUP_RATIO : height;
  const markScale = isLockup ? LOCKUP_MARK_SIZE / MARK_SIZE : 1;

  const a11yProps = title
    ? { role: 'img', 'aria-labelledby': titleId }
    : { 'aria-hidden': true };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={width}
      height={height}
      focusable="false"
      {...a11yProps}
      {...svgProps}
    >
      {title && <title id={titleId}>{title}</title>}
      <g transform={`scale(${markScale})`}>
        <rect width={MARK_SIZE} height={MARK_SIZE} fill={brand.deepGreen} />
        {MARK_RAYS.map((d) => (
          <path key={d.slice(0, 16)} d={d} fill={brand.primary} />
        ))}
      </g>
      {isLockup &&
        WORDMARK.map((d) => (
          <path key={d.slice(0, 16)} d={d} fill={WORDMARK_COLOR[tone]} />
        ))}
    </svg>
  );
}
